import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import MainSidebar from '../../components/ui/MainSidebar';
import { emailTemplateService } from '../../services/emailTemplateService';
import TemplateLibrary from './components/TemplateLibrary';
import TemplateEditor from './components/TemplateEditor';
import TemplatePreview from './components/TemplatePreview';
import SchedulingPanel from './components/SchedulingPanel';
import SegmentFilter from './components/SegmentFilter';
import { trackEmailCampaignEvent, trackUserBehaviorFunnel } from '../../utils/analytics';

const EmailTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeView, setActiveView] = useState('library');
  const [filters, setFilters] = useState({ segment: '', status: 'active' });
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [filters]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await emailTemplateService?.getAllTemplates(filters);
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    trackUserBehaviorFunnel('template_creation_started', {
      funnel_stage: 'create_template'
    });
    setSelectedTemplate(null);
    setActiveView('editor');
  };

  const handleEditTemplate = (template) => {
    trackEmailCampaignEvent('template_edit_started', {
      template_id: template?.id,
      template_name: template?.templateName,
      segment: template?.segment
    });
    setSelectedTemplate(template);
    setActiveView('editor');
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailTemplateService?.deleteTemplate(templateId);
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await emailTemplateService?.duplicateTemplate(templateId);
      await loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template');
    }
  };

  const handlePreview = (template) => {
    trackUserBehaviorFunnel('template_preview_viewed', {
      funnel_stage: 'preview_template',
      template_id: template?.id,
      template_name: template?.templateName
    });
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleSchedule = (template) => {
    trackUserBehaviorFunnel('template_scheduling_started', {
      funnel_stage: 'schedule_campaign',
      template_id: template?.id,
      template_name: template?.templateName,
      segment: template?.segment
    });
    setSelectedTemplate(template);
    setShowScheduling(true);
  };

  const handleSaveTemplate = async () => {
    trackEmailCampaignEvent('template_saved', {
      goal_type: 'template_creation'
    });
    await loadTemplates();
    setActiveView('library');
    setSelectedTemplate(null);
  };

  const handleCancelEdit = () => {
    setActiveView('library');
    setSelectedTemplate(null);
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      <div className="flex-1 overflow-auto ml-64">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Email Template Management
              </h1>
              <p className="text-muted-foreground">
                Create and manage preset email templates for different client segments
              </p>
            </div>
            {activeView === 'library' && (
              <button
                onClick={handleCreateTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring"
              >
                <Icon name="Plus" size={20} />
                <span>New Template</span>
              </button>
            )}
          </div>

          {activeView === 'library' && (
            <>
              <SegmentFilter filters={filters} onFilterChange={setFilters} />
              <TemplateLibrary
                templates={templates}
                loading={loading}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
                onDuplicate={handleDuplicateTemplate}
                onPreview={handlePreview}
                onSchedule={handleSchedule}
              />
            </>
          )}

          {activeView === 'editor' && (
            <TemplateEditor
              template={selectedTemplate}
              onSave={handleSaveTemplate}
              onCancel={handleCancelEdit}
            />
          )}
        </div>
      </div>

      {showPreview && selectedTemplate && (
        <TemplatePreview
          template={selectedTemplate}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showScheduling && selectedTemplate && (
        <SchedulingPanel
          template={selectedTemplate}
          onClose={() => setShowScheduling(false)}
          onScheduled={loadTemplates}
        />
      )}
    </div>
  );
};

export default EmailTemplateManagement;